import styled, { css, DefaultTheme } from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import { SINGLE_FILTERS_ROW_BREAKPOINT } from "@shared/features/filters/constants";
import Input from "common/Inputs/Input";
import { StylableButton } from "common/Buttons/Button";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

/**
 * New look: filter chips are quiet 32px pills with a soft border — the same
 * family as the sort control — and filled without a border on phones. An
 * applied filter turns blue.
 */
export const getNewLookChipColors = (
  theme: DefaultTheme,
  active?: boolean
) => ({
  background: getChipBackground(theme, active),
  border: `1px solid ${getChipBorderColor(theme, active)}`,
  color: active ? theme.colors.primaryBlue : theme.colors.titleColor,
});

const getChipBackground = (theme: DefaultTheme, active?: boolean) => {
  if (active) return theme.colors.lightBlue;
  return theme.isPhone
    ? theme.colors.filterChipsColor
    : theme.colors.cardBgColor;
};

const getChipBorderColor = (theme: DefaultTheme, active?: boolean) => {
  if (theme.isPhone) return "transparent";
  return active
    ? hexToRGB(theme.colors.primaryBlue, 0.45)
    : theme.colors.cardBorderColor;
};

export const FilterChipsWrapper = styled.div<{
  $showingBottomNavigator?: boolean;
  $addBorderTop?: boolean;
}>`
  flex: none;
  @media (max-width: ${SINGLE_FILTERS_ROW_BREAKPOINT}) {
    padding: 0 12px;
    display: flex;
    justify-content: flex-start;
    overflow-x: auto;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  ${(p) =>
    p.$showingBottomNavigator &&
    `
      padding: 6px 12px 6px 8px !important;
      border-bottom: 1px solid ${p.theme.colors.lightBorderGrey}; 
    `};

  ${(p) =>
    p.$addBorderTop &&
    `
      border-top: 1px solid ${p.theme.colors.lightBorderGrey}; 
  `};
  ${(p) =>
    p.theme.isNewLook &&
    !p.$showingBottomNavigator &&
    css`
      @media (max-width: ${SINGLE_FILTERS_ROW_BREAKPOINT}) {
        -webkit-mask-image: linear-gradient(
          to right,
          #000 calc(100% - 28px),
          transparent
        );
        mask-image: linear-gradient(
          to right,
          #000 calc(100% - 28px),
          transparent
        );
      }
    `}
  ${(p) =>
    p.theme.isNewLook &&
    p.$showingBottomNavigator &&
    css`
      /* 32px chips + 6px above and below: one 44px strip. */
      padding: 6px 16px !important;
      border-bottom: ${p.theme.borders.lightest};
      ${p.$addBorderTop && `border-top: ${p.theme.borders.lightest};`}
    `}
`;

export const StyledInput = styled(Input)<{
  $showClear?: boolean;
  $isSingleRowView?: boolean;
  $hasValue?: boolean;
}>`
  cursor: pointer;
  input {
    cursor: pointer;
  }
  .ant-input-suffix {
    display: none;
  }
  ${(p) =>
    p.$showClear &&
    `
    .ant-input-suffix {
      display: block;
    }
    .ant-input-clear-icon {
      visibility: visible;
    }
  `};
  ${(p) =>
    p.$isSingleRowView &&
    `
    height: 32px;
    background: ${p.theme.colors.filterChipsColor} !important;
    border: none !important;
    border-radius: 50px !important;
    box-shadow: none !important;
    text-align: center; 
    input.ant-input::placeholder, &.ant-input::placeholder {
      color: ${p.theme.colors.primaryGrey} !important; 
      text-align: center; 
    }
   
    &&& {
      &.ant-input, .ant-input {
        background: ${p.theme.colors.filterChipsColor} !important;
        text-align: center; 
      }
    }
    .ant-input-clear-icon {
      background: ${p.theme.colors.filterChipsColor} !important;
      color: ${p.theme.colors.lightestGrey} !important;
    }
  `};

  ${(p) => {
    if (!p.theme.isNewLook || !p.$isSingleRowView) return "";
    const chip = getNewLookChipColors(p.theme);
    return css`
      &&& {
        &.ant-input,
        .ant-input {
          background: ${chip.background} !important;
          color: ${chip.color} !important;
          font-size: 13px;
        }
      }
      &&& {
        border: ${chip.border} !important;
        border-radius: ${p.theme.radii.control}px !important;
        background: ${chip.background} !important;
      }
    `;
  }};
  @media (max-width: ${phoneMaxWidth}) {
    ${(p) =>
      p.$hasValue &&
      p.theme.isNewLook &&
      css`
        &&& {
          border: ${getNewLookChipColors(p.theme, true).border} !important;
          background: ${p.theme.colors.lightBlue} !important;
          &.ant-input,
          .ant-input {
            background: ${p.theme.colors.lightBlue} !important;
            color: ${p.theme.colors.primaryBlue} !important;
          }
        }
      `}
    ${(p) =>
      p.$hasValue &&
      !p.theme.isNewLook &&
      `
    &&& {
     &.ant-input {
     background: ${p.theme.colors.filterChipsActiveColor} !important;
     color: ${p.theme.colors.white} !important;
     }
    }
  `};
  }
`;

export const MobileResetButton = styled(StylableButton).attrs({
  type: "primary",
})`
  width: 100%;
  height: 40px;
`;
